import { defineStore } from "pinia";
import productImage from "/images/product_categories/OTROS.png";
import { recategorizeData, categoriesImages } from "@/types/apps/EcommerceType";
import type { TatCartItem, TatOrder, TatParkedDraft, TatProduct, TatStore } from "~/types/apps/TatType";

function resolveDisplayImage(item: TatProduct): { image: string; categoryImage: string } {
    const findCategory = recategorizeData.find((data: any) => data.category === item.category);
    const newCategory = findCategory && findCategory.group ? findCategory.group : item.category;
    const findImage = categoriesImages.find((data: any) => data.category === newCategory);
    const categoryImage = findImage && findImage.image ? findImage.image : productImage;
    return {
        image: item.image ? item.image : categoryImage,
        categoryImage,
    };
}

function toNumber(value: number | string | undefined): number {
    return value != null ? Number(value) : 0;
}

function round6(value: number): number {
    // El backend guarda lat/lng con máximo 6 decimales; el GPS del navegador
    // suele entregar más precisión de la que el campo acepta.
    return Math.round(value * 1e6) / 1e6;
}

function todayIso(): string {
    return new Date().toISOString().slice(0, 10);
}

function getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error("Geolocalización no disponible en este dispositivo."));
            return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
        });
    });
}

export const useTatCartStore = defineStore({
    id: "tatCart",
    state: () => ({
        store: null as TatStore | null,
        products: [] as TatProduct[],
        productsLoading: false,
        productsCount: 0,
        page: 1,
        pageSize: 20,
        search: "" as string | null,
        category: [] as string[],
        price: null as string | null, // "min:max"
        discountRange: null as string | null, // "min:max" (% de descuento)
        hasDiscount: null as boolean | null,
        hasStock: null as boolean | null,
        showAddedOnly: false as boolean,
        ordering: "price" as string | null,
        layout: "grid" as "grid" | "list",
        isProductImageDisplayed: true as boolean,
        cart: [] as TatCartItem[],
        activeRouteStopId: null as number | null,
        editingOrderId: null as number | null,
        startLatitude: null as number | null,
        startLongitude: null as number | null,
        startedAt: null as string | null,
        // Distinto de `startedAt` (que se captura al ABRIR el formulario,
        // tenga o no items) — este marca cuándo se agregó el primer producto,
        // que es lo que de verdad indica "empezó a diligenciarse un pedido".
        // Sirve para mostrar el pedido en curso en el listado de visitas y
        // permitir continuarlo o descartarlo.
        draftStartedAt: null as string | null,
        // Fecha (YYYY-MM-DD) en que se inició el borrador — un pedido sin
        // enviar solo se aloja por el día en que se empezó a digitar; al
        // otro día se descarta solo (ver expireStaleDraft()).
        draftDate: null as string | null,
        // Cliente dueño del borrador, capturado aparte de tatClients.selected
        // (que cambia con solo mirar la ficha de otro cliente) para que el
        // listado de visitas sepa a quién pertenece el pedido sin terminar.
        draftThirdpartyId: null as number | null,
        draftClientName: null as string | null,
        // Borradores de OTROS clientes, aparcados al cambiar de cliente sin
        // descartarlos — permite tener varios pedidos sin enviar el mismo
        // día (ver prepareDraft/parkCurrentDraft más abajo).
        parkedDrafts: [] as TatParkedDraft[],
        // Parada de ruta objetivo del PRÓXIMO borrador a abrir en Pedido.vue
        // (fijada por quien navega hacia allá, ej. Ruta.vue). Se consume y
        // limpia en prepareDraft — no es el `activeRouteStopId` del borrador
        // que ya está cargado en `cart`, para no pisarlo antes de aparcarlo.
        pendingRouteStopId: null as number | null,
        subtotal: 0,
        discount: 0,
        taxes: 0,
        total: 0,
        submitting: false,
        submitError: "" as string,
    }),
    getters: {
        totalQty(state) {
            return state.cart.reduce((sum, item) => sum + item.qty, 0);
        },
        displayedProducts(state) {
            return state.showAddedOnly ? state.cart.map((item) => item.product) : state.products;
        },
        displayedCount(state) {
            return state.showAddedOnly ? state.cart.length : state.productsCount;
        },
        // `editingOrderId` se excluye a propósito: editar un pedido ya
        // enviado también deja `cart` con items, pero eso no es un borrador.
        hasDraft(state) {
            return state.cart.length > 0 && state.editingOrderId === null;
        },
        // Todos los pedidos sin enviar del día: el que está abierto ahora
        // mismo en `cart` (si hay uno) más los aparcados de otros clientes.
        allDrafts(state): TatParkedDraft[] {
            const working: TatParkedDraft[] =
                state.cart.length > 0 && state.editingOrderId === null && state.draftThirdpartyId != null
                    ? [
                          {
                              thirdpartyId: state.draftThirdpartyId,
                              clientName: state.draftClientName ?? "",
                              routeStopId: state.activeRouteStopId,
                              cart: state.cart,
                              draftStartedAt: state.draftStartedAt ?? new Date().toISOString(),
                              draftDate: state.draftDate ?? todayIso(),
                          },
                      ]
                    : [];
            return [...working, ...state.parkedDrafts];
        },
    },
    actions: {
        async fetchMyStore() {
            if (this.store) return this.store;
            const baseUrl = useRuntimeConfig().public.apiBase;
            const response = await fetchWrapper.get(
                `${baseUrl}/api/v1/tat/seller-store/me/`,
            );
            this.store = response.store;
            return this.store;
        },
        async fetchProducts() {
            if (!this.store) await this.fetchMyStore();
            const baseUrl = useRuntimeConfig().public.apiBase;
            this.productsLoading = true;
            try {
                const params = new URLSearchParams({
                    page: String(this.page),
                    page_size: String(this.pageSize),
                });
                if (this.search) params.set("search", this.search);
                if (this.category.length) params.set("category_in", this.category.join(","));
                if (this.price) {
                    const [min, max] = this.price.split(":");
                    params.set("min_price", min);
                    params.set("max_price", max);
                }
                if (this.discountRange) {
                    const [min, max] = this.discountRange.split(":");
                    params.set("min_discount", min);
                    params.set("max_discount", max);
                }
                if (this.hasDiscount !== null) params.set("has_discount", String(this.hasDiscount));
                if (this.hasStock !== null) params.set("has_stock", String(this.hasStock));
                if (this.ordering) params.set("ordering", this.ordering);

                const response = await fetchWrapper.get(
                    `${baseUrl}/api/v1/management/stores/${this.store!.id}/products/?${params.toString()}`,
                );
                const results = (response.results ?? []) as TatProduct[];
                for (const item of results) {
                    const { image, categoryImage } = resolveDisplayImage(item);
                    item.image = image;
                    item.categoryImage = categoryImage;
                }
                this.products = results;
                this.productsCount = response.count ?? 0;
                return this.products;
            } finally {
                this.productsLoading = false;
            }
        },
        setSearch(value: string | null) {
            this.search = value;
        },
        setOrdering(value: string | null) {
            this.ordering = value;
        },
        setLayout(value: "grid" | "list") {
            this.layout = value;
        },
        setProductImageDisplayed(value: boolean) {
            this.isProductImageDisplayed = value;
        },
        setCategory(value: string[]) {
            this.category = value;
        },
        setPrice(value: string | null) {
            this.price = value;
        },
        setDiscountRange(value: string | null) {
            this.discountRange = value;
        },
        setHasDiscount(value: boolean | null) {
            this.hasDiscount = value;
        },
        setHasStock(value: boolean | null) {
            this.hasStock = value;
        },
        setShowAddedOnly(value: boolean) {
            this.showAddedOnly = value;
        },
        // Handoff transitorio hacia Pedido.vue: quien navega hacia allá deja
        // aquí la parada objetivo, y prepareDraft() la consume al abrir el
        // formulario. No tocar `activeRouteStopId` directamente desde afuera
        // — pertenece al borrador que esté cargado en `cart`, y pisarlo antes
        // de aparcarlo perdería a qué parada correspondía ese borrador.
        setPendingRouteStop(routeStopId: number | null) {
            this.pendingRouteStopId = routeStopId;
        },
        // Un borrador de más de un día se descarta solo: evita que el
        // vendedor retome sin darse cuenta un pedido de ayer con precios/
        // stock desactualizados. Se llama una vez al entrar al área de
        // vendedor (ver layouts/TatSeller.vue).
        expireStaleDraft() {
            const today = todayIso();
            if (this.draftDate && this.draftDate !== today) {
                this.clearCart();
            }
            this.parkedDrafts = this.parkedDrafts.filter((d) => d.draftDate === today);
        },
        // Guarda el borrador actualmente abierto en `cart` (si hay uno) en
        // `parkedDrafts`, y limpia el área de trabajo — sin descartar nada.
        // Permite tener varios pedidos sin enviar al tiempo: se usa antes de
        // cargar el borrador/pedido de OTRO cliente en `cart`.
        parkCurrentDraft() {
            if (this.cart.length === 0 || this.editingOrderId !== null || this.draftThirdpartyId == null) return;
            this.parkedDrafts = this.parkedDrafts.filter((d) => d.thirdpartyId !== this.draftThirdpartyId);
            this.parkedDrafts.push({
                thirdpartyId: this.draftThirdpartyId,
                clientName: this.draftClientName ?? "",
                routeStopId: this.activeRouteStopId,
                cart: this.cart,
                draftStartedAt: this.draftStartedAt ?? new Date().toISOString(),
                draftDate: this.draftDate ?? todayIso(),
            });
            this.cart = [];
            this.activeRouteStopId = null;
            this.draftStartedAt = null;
            this.draftDate = null;
            this.draftThirdpartyId = null;
            this.draftClientName = null;
            this.subtotal = this.discount = this.taxes = this.total = 0;
        },
        // Trae de vuelta a `cart` un borrador previamente aparcado de ese
        // cliente, si existe. Devuelve true si encontró y restauró uno.
        restoreParkedDraft(thirdpartyId: number): boolean {
            const parked = this.parkedDrafts.find((d) => d.thirdpartyId === thirdpartyId);
            if (!parked) return false;
            this.parkedDrafts = this.parkedDrafts.filter((d) => d.thirdpartyId !== thirdpartyId);
            this.cart = parked.cart;
            this.activeRouteStopId = parked.routeStopId;
            this.draftStartedAt = parked.draftStartedAt;
            this.draftDate = parked.draftDate;
            this.draftThirdpartyId = parked.thirdpartyId;
            this.draftClientName = parked.clientName;
            this.updateTotals();
            return true;
        },
        // Punto de entrada al abrir Pedido.vue para un pedido nuevo (no una
        // edición): decide si sigue con el borrador que ya está en `cart`
        // (mismo cliente), si aparca ese borrador y restaura/crea el de este
        // cliente, y aplica la parada objetivo dejada en `pendingRouteStopId`.
        prepareDraft(thirdparty: { id: number; social_reason?: string; first_name?: string; first_surname?: string }) {
            if (this.editingOrderId !== null) {
                this.pendingRouteStopId = null;
                return;
            }
            const continuingSameClient = this.hasDraft && this.draftThirdpartyId === thirdparty.id;
            if (!continuingSameClient) {
                this.parkCurrentDraft();
                const restored = this.restoreParkedDraft(thirdparty.id);
                if (!restored) {
                    this.cart = [];
                    this.subtotal = this.discount = this.taxes = this.total = 0;
                    this.draftStartedAt = null;
                    this.draftDate = null;
                    this.activeRouteStopId = this.pendingRouteStopId;
                }
            }
            this.pendingRouteStopId = null;
            this.draftThirdpartyId = thirdparty.id;
            this.draftClientName =
                thirdparty.social_reason || `${thirdparty.first_name ?? ''} ${thirdparty.first_surname ?? ''}`.trim();
        },
        // Descarta el pedido sin enviar de ese cliente, esté abierto en
        // `cart` en este momento o aparcado.
        discardDraft(thirdpartyId: number) {
            if (this.draftThirdpartyId === thirdpartyId && this.hasDraft) {
                this.clearCart();
            } else {
                this.parkedDrafts = this.parkedDrafts.filter((d) => d.thirdpartyId !== thirdpartyId);
            }
        },
        async captureStart() {
            // TT-124: registra dónde y cuándo el vendedor abrió el formulario
            // de "Iniciar Pedido / Atención Directa", para poder auditar el
            // tiempo real de atención (inicio vs. fin, capturado en submitOrder).
            // Falla en silencio: no debe bloquear al vendedor si el GPS no
            // responde al abrir, solo se exige de verdad al confirmar el pedido.
            this.startLatitude = null;
            this.startLongitude = null;
            this.startedAt = new Date().toISOString();
            try {
                const position = await getCurrentPosition();
                this.startLatitude = round6(position.coords.latitude);
                this.startLongitude = round6(position.coords.longitude);
            } catch {
                // sin ubicación de inicio; se reintenta la de cierre en submitOrder
            }
        },
        setPage(value: number) {
            this.page = value;
        },
        filterReset() {
            this.category = [];
            this.price = null;
            this.discountRange = null;
            this.hasDiscount = null;
            this.hasStock = null;
            this.showAddedOnly = false;
            this.search = null;
            this.page = 1;
            this.ordering = "price";
        },
        addToCart(product: TatProduct, qty: number = 1) {
            if (this.cart.length === 0) {
                this.draftStartedAt = new Date().toISOString();
                this.draftDate = todayIso();
            }
            const existing = this.cart.find((item) => item.product.id === product.id);
            if (existing) {
                existing.qty = qty;
            } else {
                this.cart.push({
                    product,
                    qty,
                    discount_percent: toNumber(product.discount_percent),
                    discount_amount: toNumber(product.discount_amount),
                    net_price: 0,
                });
            }
            this.updateTotals();
        },
        updateQty(productId: number, qty: number) {
            const item = this.cart.find((i) => i.product.id === productId);
            if (!item) return;
            if (qty <= 0) {
                this.removeFromCart(productId);
                return;
            }
            item.qty = qty;
            this.updateTotals();
        },
        removeFromCart(productId: number) {
            this.cart = this.cart.filter((i) => i.product.id !== productId);
            this.updateTotals();
        },
        clearCart() {
            this.cart = [];
            this.activeRouteStopId = null;
            this.editingOrderId = null;
            this.startLatitude = null;
            this.startLongitude = null;
            this.startedAt = null;
            this.draftStartedAt = null;
            this.draftDate = null;
            this.draftThirdpartyId = null;
            this.draftClientName = null;
            this.subtotal = this.discount = this.taxes = this.total = 0;
        },
        async loadOrderForEdit(order: TatOrder) {
            // Editar un pedido reutiliza el mismo flujo de "Tomar pedido": se
            // reconstruye el carrito a partir de los items guardados, buscando
            // cada producto por EAN (el lookup que ya soporta ese endpoint) para
            // tener la info completa (imagen, categoría, stock) y no solo el
            // snapshot congelado en el pedido.
            // Si había un pedido sin enviar de OTRO cliente abierto, se
            // aparca en vez de perderse al cargar aquí los items del pedido
            // que se va a editar.
            this.parkCurrentDraft();
            if (!this.store) await this.fetchMyStore();
            const baseUrl = useRuntimeConfig().public.apiBase;
            this.editingOrderId = order.id ?? null;
            this.activeRouteStopId = order.route_stop ?? null;

            const cartItems: TatCartItem[] = [];
            for (const item of order.items) {
                try {
                    const product = (await fetchWrapper.get(
                        `${baseUrl}/api/v1/management/stores/${this.store!.id}/products/${item.ean}/`,
                    )) as TatProduct;
                    const { image, categoryImage } = resolveDisplayImage(product);
                    product.image = image;
                    product.categoryImage = categoryImage;
                    cartItems.push({
                        product,
                        qty: toNumber(item.qty),
                        discount_percent: toNumber(item.discount_percent),
                        discount_amount: toNumber(item.discount_amount),
                        net_price: toNumber(item.net_price),
                    });
                } catch {
                    // El producto pudo desactivarse/eliminarse desde el pedido original;
                    // se omite del carrito editable en vez de romper la carga completa.
                }
            }
            this.cart = cartItems;
            this.updateTotals();
        },
        updateTotals() {
            // El precio del producto ya viene con IVA incluido; el campo `iva` es solo
            // informativo (se muestra en el catálogo/carrito) y no se usa en ningún cálculo.
            let subtotal = 0;
            let discount = 0;
            for (const item of this.cart) {
                const price = toNumber(item.product.price);
                const grossTotal = price * item.qty;
                const lineDiscount =
                    (grossTotal * item.discount_percent) / 100 + item.discount_amount;
                const netBase = Math.max(0, grossTotal - lineDiscount);
                item.net_price = Math.round(netBase * 100) / 100;

                subtotal += netBase;
                discount += lineDiscount;
            }
            this.subtotal = Math.round(subtotal * 100) / 100;
            this.discount = Math.round(discount * 100) / 100;
            this.taxes = 0;
            this.total = this.subtotal;
        },
        async submitOrder(thirdpartyId: number, routeStopId?: number | null) {
            this.submitError = "";
            this.submitting = true;
            try {
                const baseUrl = useRuntimeConfig().public.apiBase;
                const itemsPayload = this.cart.map((item) => ({
                    product_id: item.product.id,
                    qty: item.qty,
                    unit_price: toNumber(item.product.price),
                    discount_percent: item.discount_percent,
                    discount_amount: item.discount_amount,
                    tax_percent: toNumber(item.product.iva),
                    net_price: item.net_price,
                }));

                if (this.editingOrderId) {
                    const response = await fetchWrapper.patch(
                        `${baseUrl}/api/v1/tat/orders/${this.editingOrderId}/`,
                        { items: itemsPayload },
                    );
                    this.clearCart();
                    return response;
                }

                let position: GeolocationPosition;
                try {
                    position = await getCurrentPosition();
                } catch (e) {
                    this.submitError =
                        "No se pudo obtener tu ubicación. Actívala e intenta de nuevo.";
                    throw e;
                }

                const payload = {
                    thirdparty: thirdpartyId,
                    ...(routeStopId ? { route_stop: routeStopId } : {}),
                    latitude: round6(position.coords.latitude),
                    longitude: round6(position.coords.longitude),
                    // TT-124: coordenadas/hora de inicio de atención, capturadas
                    // al abrir el formulario (ver captureStart()), para auditoría
                    // de tiempos en sitio. El backend puede ignorarlas si aún no
                    // las soporta.
                    ...(this.startLatitude != null ? { start_latitude: this.startLatitude } : {}),
                    ...(this.startLongitude != null ? { start_longitude: this.startLongitude } : {}),
                    ...(this.startedAt ? { started_at: this.startedAt } : {}),
                    items: itemsPayload,
                };
                const response = await fetchWrapper.post(
                    `${baseUrl}/api/v1/tat/orders/`,
                    payload,
                );
                this.clearCart();
                return response;
            } finally {
                this.submitting = false;
            }
        },
    },
    persist: {
        storage: persistedState.localStorage,
        // Los filtros/búsqueda del catálogo NO se persisten: deben partir
        // limpios cada vez que se entra a tomar/editar un pedido. Solo el
        // carrito en curso (para no perderlo si se navega) y las preferencias
        // de visualización (igual que el modo oscuro) sobreviven.
        paths: [
            'cart',
            'activeRouteStopId',
            'editingOrderId',
            'draftStartedAt',
            'draftDate',
            'draftThirdpartyId',
            'draftClientName',
            'parkedDrafts',
            'layout',
            'isProductImageDisplayed',
        ],
    },
});
